import React, { useState, useMemo } from 'react';
import { getServices } from '../data/services.index';
import { useLanguage } from '../context/LanguageContext';

function Services() {
  const { lang, t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');

  const services = useMemo(() => getServices(lang), [lang]);

  const flagship = useMemo(() => services.find((s) => s.isFlagship) || services[0], [services]);
  const otherServices = useMemo(() => services.filter((s) => !s.isFlagship), [services]);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(otherServices.map((s) => s.category).filter(Boolean)));
    return ['all', ...cats];
  }, [otherServices]);

  const filteredServices = useMemo(() => {
    if (activeCategory === 'all') return otherServices;
    return otherServices.filter((s) => s.category === activeCategory);
  }, [otherServices, activeCategory]);

  return (
    <div className="space-y-16 animate-fade-in text-white">
      {/* Header */}
      <div className="space-y-4 pb-2 border-b border-white/10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-xs font-semibold tracking-wide">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse"></span>
          {lang === 'ar' ? 'منصات وأدوات مفتوحة المصدر' : 'Open Source Platforms & Developer Utilities'}
        </div>
        <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">
          {t('sections.servicesTitle')}
        </h1>
        <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
          {t('sections.servicesDesc')}
        </p>
      </div>

      {/* Flagship Platform Hero Spotlight (Trax) */}
      {flagship && (
        <section className="relative p-8 md:p-12 rounded-[2.5rem] bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-white/[0.01] border-2 border-primary-500/30 hover:border-primary-500/50 transition-all duration-300 shadow-2xl overflow-hidden group">
          {/* Subtle decorative glow */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl -z-10 group-hover:bg-primary-500/15 transition-all"></div>

          <div className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-widest bg-primary-500/20 text-primary-300 border border-primary-500/40">
                    {lang === 'ar' ? 'المنصة الرائدة • مبنية بلغة Rust' : 'Flagship Platform • Built in Rust'}
                  </span>
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                </div>
                <h2 className="text-2xl md:text-4xl font-extrabold text-white tracking-tight">
                  {flagship.title}
                </h2>
                <p className="text-sm md:text-base text-primary-300 font-medium">
                  {flagship.tagline}
                </p>
              </div>

              <a
                href={flagship.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all shadow-lg shadow-primary-500/25 self-start shrink-0"
              >
                <span>{lang === 'ar' ? 'استعراض المشروع على GitHub' : 'View on GitHub'}</span>
                <svg className="w-4 h-4 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-4xl font-normal">
              {flagship.description}
            </p>

            {/* Feature Highlights Grid */}
            {flagship.features && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {flagship.features.map((feature, fIdx) => (
                  <div
                    key={fIdx}
                    className="p-4 rounded-xl bg-white/[0.03] border border-white/10 flex items-start gap-3"
                  >
                    <svg className="w-5 h-5 text-primary-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-xs sm:text-sm text-white/85 leading-snug font-medium">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Stack Tags */}
            <div className="pt-4 border-t border-white/10 flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-white/50 mr-2 rtl:ml-2 rtl:mr-0">
                {lang === 'ar' ? 'التقنيات المستخدمة:' : 'Core Stack:'}
              </span>
              {flagship.tags.map((tag, tIdx) => (
                <span
                  key={tIdx}
                  className="px-3 py-1 text-xs font-semibold rounded-lg bg-white/[0.05] border border-white/10 text-white/90"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other Engineering Utilities Section */}
      <section className="space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
              {lang === 'ar' ? 'الأدوات والخدمات المساعدة' : 'Engineering Utilities & Micro-Tools'}
            </h2>
            <p className="text-sm text-white/60">
              {lang === 'ar'
                ? 'مجموعة من الأدوات المتخصصة في الشبكات، اختبار الأداء، والأمان'
                : 'Specialized tools in networking, load testing, security, and developer productivity'}
            </p>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
                  activeCategory === cat
                    ? 'bg-primary-500 text-white shadow-md shadow-primary-500/20'
                    : 'bg-white/[0.03] border border-white/10 text-white/70 hover:text-white hover:bg-white/[0.06]'
                }`}
              >
                {cat === 'all'
                  ? (lang === 'ar' ? 'كافة الأدوات' : 'All Utilities')
                  : cat}
              </button>
            ))}
          </div>
        </div>

        {/* Utilities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service, index) => (
            <a
              key={index}
              href={service.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group p-6 rounded-3xl bg-white/[0.03] border border-white/10 hover:border-primary-500/40 hover:bg-white/[0.05] transition-all duration-300 flex flex-col justify-between space-y-5 shadow-lg"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-primary-400 bg-primary-500/10 px-2.5 py-0.5 rounded-md border border-primary-500/20">
                    {service.category || 'Tool'}
                  </span>
                  <svg className="w-4 h-4 text-white/40 group-hover:text-primary-300 transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-white group-hover:text-primary-300 transition-colors">
                    {service.title}
                  </h3>
                  {service.tagline && (
                    <p className="text-xs text-white/60 font-medium mt-0.5">
                      {service.tagline}
                    </p>
                  )}
                </div>

                <p className="text-white/75 text-sm leading-relaxed line-clamp-3">
                  {service.description}
                </p>
              </div>

              {service.tags && (
                <div className="pt-3 border-t border-white/10 flex flex-wrap gap-1.5 mt-auto">
                  {service.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-white/[0.04] text-white/80 border border-white/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

export default Services;
